import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, Injector, inject, provideAppInitializer } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, provideRouter, RouteReuseStrategy, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { LOCALE_ID, DEFAULT_CURRENCY_CODE } from '@angular/core';
import { environment } from '../environments/environment';
import { providePrimeNG } from 'primeng/config';
import { DefaultStyles, NotificationService } from 'core-lib';


//translation
import { provideTranslateHttpLoader, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';

import { GALLERY_CONFIG, GalleryConfig } from 'ng-gallery';
import { LIGHTBOX_CONFIG, LightboxConfig } from 'ng-gallery/lightbox';
import { routes } from './app.routes';
import { IMAGE_CONFIG, LOCATION_INITIALIZED } from '@angular/common';
import { ClientInitializerService, LanguageService, LoaderService, PIMService, SettingsService, AnalyticsService } from 'core-lib';
import { httpAuthInterceptor } from 'core-lib';

class CustomRouteReuseStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.data['reuseComponent'] ?? true;
  }
}

function stopLoaderAndSetPageLoadError() {
  let error = document.createElement("div");
  error.classList.add("page-load-error");
  error.innerHTML = "<p><b>ERROR</b><p><p>Something went wrong. Please reload the page and try again.</p>";
  document.querySelector('body')?.appendChild(error);
  document.querySelector('#hlx-app-loader')?.classList.add('loading-done');
}

export function appInitializerFactory(
  languageService: LanguageService,
  settingsService: SettingsService,
  pimService: PIMService,
  clientInitializer: ClientInitializerService,
  loaderService: LoaderService,
  analyticsService: AnalyticsService,
  notificationService: NotificationService
) {

  /*
    This function runs before anything else. Used for making sure that the translations are loaded before app starts.
  */

  return () => new Promise<any>((resolve: any) => {
    
    let start = new Date();

    settingsService.onAppInit().then((success) => {
      if (!success) {
        stopLoaderAndSetPageLoadError();
        return;
      }
      console.log('settings loaded');

      languageService.onAppInit().then(() => {
        console.log('language loaded');

        Promise.all([
          pimService.onAppInit().then((result) => result),
          clientInitializer.applyClientStyles().then((result) => result),
          clientInitializer.applyExternalScriptsBookingEngine(() => analyticsService.onAppInit()).then((result) => result),
          notificationService.onAppInit().then((result) => result)
        ])
        .then((results) => {
          const success  = results.every(result => result === true);
          if (!success) {
            stopLoaderAndSetPageLoadError();
            return;
          }
          console.log('all client styles applied');
          setTimeout(() => {
            document.querySelector('#hlx-app-loader')?.classList.add('loading-done');

            let finish = new Date();
            console.log('loading done in: ', (finish.getTime() - start.getTime()) / 1000, 'seconds');
          },500);
       
          resolve();

        });
      });
    });
      
  });
}


export const appConfig: ApplicationConfig = {
  providers: [
    {provide: 'environment', useValue: environment},
    provideZoneChangeDetection({ eventCoalescing: true }), 
    {provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy},
    provideRouter(routes, 
      withRouterConfig({onSameUrlNavigation: 'reload'}),
      withInMemoryScrolling({scrollPositionRestoration: 'top'})
    ),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        httpAuthInterceptor
      ])
    ),
    
    { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true, disableImageLazyLoadWarning: true } }, //disables image size warning
    {provide: LOCALE_ID, useValue: 'fi-FI' },
    {provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },

    provideTranslateService({
      loader: provideTranslateHttpLoader({prefix:'./translations/', suffix:'.json'}),
      fallbackLang: 'en'
    }),
    provideAppInitializer(() => {
        const initializerFn = (appInitializerFactory)(inject(LanguageService), inject(SettingsService), inject(PIMService), inject(ClientInitializerService), inject(LoaderService), inject(AnalyticsService), inject(NotificationService));
        return initializerFn();
      }),

    {
      provide: GALLERY_CONFIG,
      useValue: {
        counter: false,
        navIcon: '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368"><path d="m517.85-480-184-184L376-706.15 602.15-480 376-253.85 333.85-296l184-184Z"/></svg>'
      } as GalleryConfig
    },
    providePrimeNG({
      theme: {
        preset: DefaultStyles,
        options: {
          prefix: 'p',
          darkModeSelector: '.app-dark-mode',
          cssLayer: false
        }
      }
    })
    
  ]
};

